import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from "@mui/material";
import {
  HOSPITAL_ADDRESS,
  HOSPITAL_NAME,
  HOSPITAL_PHONE,
  LOGO_URL,
} from "../../../../config";
import { useTranslation } from "react-i18next";

const FinancialReportPrint = ({ sections }) => {
  const { t } = useTranslation();
  const pharmacyName = t("print.pharmacyName", { defaultValue: HOSPITAL_NAME });
  const summary = sections.find((section) => section.id === "summary")?.data;
  useEffect(() => {
    const printStyles = `
      @media print {
        @page {
          size: A4;
          margin: 20mm;
        }

        body {
          font-family: 'Roboto', sans-serif;
          margin: 0;
          padding: 0;
          color: #000;
        }

        .print-container {
          padding: 20px;
          background-color: #f4f4f4;
        }

        .header {
          text-align: left;
          margin-bottom: 30px;
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
        }

        .header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: bold;
          color: #333;
        }

        .header p {
          margin: 5px 0 0;
          font-size: 14px;
          color: #333;
        }

        /* Section mimicking the ProfessionalTable container */
        .section {
          margin-bottom: 24px;
          border-radius: 2px;
          border-left: 6px solid #007174;
          background-color: #f7f7f7;
          padding: 24px;
        }

        .section h3 {
          margin: 0 0 16px;
          font-size: 1.25rem;
          color: #007174;
          font-weight: bold;
        }

        /* Table styling similar to ProfessionalTable */
        .section table {
          width: 100%;
          border-collapse: collapse;
          border-radius: 2px;
          overflow: hidden;
          background-color: #fff;
          font-size: 14px;
        }

        .section thead tr {
          background-color: #007174;
        }

        .section thead th {
          color: #fff;
          font-weight: bold;
          text-transform: uppercase;
          padding: 8px;
        }

        .section tbody tr:nth-of-type(odd) {
          background-color: #e9e9e9;
        }

        .section tbody tr:nth-of-type(even) {
          background-color: #f7f7f7;
        }

        .section td {
          padding: 8px;
          color: #333;
        }

        .footer {
          border-top: 2px solid #000;
          text-align: center;
          font-size: 12px;
          color: #666;
          padding-top: 10px;
          margin-top: 30px;
        }
      }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <Box className="print-container">
      <div className="header">
        <img
          src={LOGO_URL}
          alt={pharmacyName}
          style={{ width: "70px", height: "auto", marginBottom: "8px" }}
        />
        <h1>{pharmacyName}</h1>
        <p>
          {HOSPITAL_PHONE} {HOSPITAL_ADDRESS ? `| ${HOSPITAL_ADDRESS}` : ""}
        </p>
        <p>Generated on: {new Date().toLocaleDateString()}</p>
      </div>

      {summary ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { title: "Total Revenue", value: summary.total_income },
            { title: "Total Expenses", value: summary.total_expenses },
            { title: "Profit", value: summary.net_income },
          ].map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <div className="section">
                <h3>{item.title}</h3>
                <p style={{ fontSize: "20px", fontWeight: "bold", margin: 0 }}>
                  {Number(item.value || 0).toLocaleString()} ؋
                </p>
              </div>
            </Grid>
          ))}
        </Grid>
      ) : null}

      {/* Pharmacy sections */}
      <Grid container spacing={3}>
        {sections.map((section, index) => {
          const entries = Object.entries(section.data || {});
          return (
            <Grid item xs={12} md={6} key={index}>
              <div className="section">
                <h3>{section.title}</h3>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="left"
                          sx={{ fontWeight: "bold", color: "#fff" }}
                        >
                          Label
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontWeight: "bold", color: "#fff" }}
                        >
                          Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            No Data Available
                          </TableCell>
                        </TableRow>
                      ) : (
                        entries.map(([label, value], idx) => (
                          <TableRow key={idx}>
                            <TableCell>{label}</TableCell>
                            <TableCell>{value ?? "N/A"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Grid>
          );
        })}
      </Grid>

      <div className="footer">
        <p>© {new Date().getFullYear()} - Financial Report</p>
      </div>
    </Box>
  );
};

export default FinancialReportPrint;
